<template>
  <HgModal
    v-model="showModal"
    :closeButtonCssClass="$style.modalClose"
    :containerCssClass="$style.modalContainer"
    @close="emit('close')"
  >
    <template #title>{{ t('priceDropNotifications.modal.title') }}</template>

    <form novalidate :class="$style.form" @submit.prevent="handleFormSubmit">
      <p :class="$style.intro">{{ t('priceDropNotifications.modal.content') }}</p>

      <HgInput
        v-model.trim="formState.name"
        :class="$style.input"
        :label="t('priceDropNotifications.modal.form.priceDropAlertNameLabel')"
        :isRequired="true"
        :isInvalid="v$.name.$invalid"
        :isDirty="v$.name.$dirty"
        name="name"
        @update:modelValue="v$.name.$touch()"
      >
        <template #errorMessages>
          <span v-if="v$.name.required.$invalid">
            {{ t('priceDropNotifications.modal.form.fieldRequiredLabel') }}
          </span>
        </template>
      </HgInput>

      <div v-if="isUserLoggedIn" :class="$style.loggedInSection">
        <p :class="$style.loggedInText">
          <i18n-t keypath="priceDropNotifications.modal.form.loginCta" scope="global">
            <template #email>
              <strong>{{ formState.recipient }}</strong>
            </template>
            <template #logout>
              <a :class="$style.logoutLink" :href="signOutLink">{{
                t('priceDropNotifications.modal.form.logoutLabel')
              }}</a>
            </template>
          </i18n-t>
        </p>
      </div>

      <HgInput
        v-else
        v-model.trim="formState.recipient"
        :class="$style.input"
        type="email"
        :label="t('priceDropNotifications.modal.form.emailLabel')"
        :isRequired="true"
        :isInvalid="v$.recipient.$invalid"
        :isDirty="v$.recipient.$dirty"
        name="recipient"
        @update:modelValue="v$.recipient.$touch()"
      >
        <template #errorMessages>
          <span v-if="v$.recipient.required.$invalid">
            {{ t('validation.phoneOrEMailRequired') }}
          </span>
          <span v-if="v$.recipient.email.$invalid">
            {{ t('validation.emailInvalid') }}
          </span>
        </template>
      </HgInput>

      <p :class="$style.legalText">
        <i18n-t keypath="priceDropNotifications.modal.form.disclaimer" scope="global">
          <template #gtc>
            <a :href="t('TnCText.gtc.link')" target="_blank" rel="noopener">{{
              t('TnCText.gtc.name')
            }}</a>
          </template>
          <template #ds>
            <a :href="t('TnCText.ds.link')" target="_blank" rel="noopener">{{
              t('TnCText.ds.name')
            }}</a>
          </template>
        </i18n-t>
      </p>
    </form>

    <template #footer>
      <HgButton
        :disabled="isRequestPending"
        :class="$style.saveButton"
        data-test="save-price-drop-button"
        type="button"
        @click="handleFormSubmit"
      >
        {{ t('priceDropNotifications.modal.form.saveBtLabel') }}
      </HgButton>
    </template>
  </HgModal>
</template>

<script setup lang="ts">
import { HgButton, HgInput, HgModal } from '@smg-real-estate/domus';
import { Listing } from '@smg-real-estate/hg-rets-schema';
import { useVuelidate } from '@vuelidate/core';
import { email as emailValidator, required, requiredIf } from '@vuelidate/validators';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  isUserLoggedIn as checkUserLoggedIn,
  getUserLoginInfo,
} from '@/helpers/Authentication/authClient';
import { getListingAddress } from '@/helpers/ListingData/ListingData';
import { usePriceDropAlertStore } from '@/store/modules/price-drop-alert';
import { CreatePriceDropAlertEmitPayload } from '@/store/types/price-drop-alert-state';
import { trackSavePriceDropAlert } from './tracking';

export interface SavePriceDropModalProps {
  isModalVisible: boolean;
  listing: Listing;
}

export interface SavePriceDropModalEmits {
  close: [];
  savePriceDropAlert: [payload: CreatePriceDropAlertEmitPayload];
}

const props = defineProps<SavePriceDropModalProps>();
const emit = defineEmits<SavePriceDropModalEmits>();

const priceDropAlertStore = usePriceDropAlertStore();

const isRequestPending = computed(() => priceDropAlertStore.isRequestPending);

const { t, locale } = useI18n();

const showModal = computed({
  get: () => props.isModalVisible,
  set: () => emit('close'),
});

const isUserLoggedIn = ref(false);

const location = getListingAddress(props.listing);

const formState = reactive({
  name: location ? t('priceDropNotifications.modal.form.namePlaceholder', { location }) : '',
  recipient: '',
});

const rules = computed(() => ({
  name: {
    required,
  },
  recipient: {
    required: requiredIf(() => !isUserLoggedIn.value),
    email: emailValidator,
  },
}));

const v$ = useVuelidate(rules, formState);

const signOutLink = computed(() => {
  return `${import.meta.env.VUE_APP_BASE_URL}/${locale.value}/sign-out`;
});

const fillInAuthenticatedUserEmail = async (): Promise<void> => {
  const userInfo = (await checkUserLoggedIn()) ? await getUserLoginInfo() : null;

  isUserLoggedIn.value = !!userInfo;
  formState.recipient = userInfo?.email ?? '';
};

const submitPriceDropAlert = (): void => {
  if (!props.listing?.id) {
    return;
  }

  emit('savePriceDropAlert', {
    name: formState.name,
    recipient: formState.recipient,
    listingId: props.listing.id,
  });

  trackSavePriceDropAlert();
};

const handleFormSubmit = (): void => {
  v$.value.$touch();

  if (v$.value.$invalid) {
    return;
  }

  submitPriceDropAlert();
};

watch(
  () => props.isModalVisible,
  (newValue: boolean) => {
    if (newValue) {
      fillInAuthenticatedUserEmail();
    }
  },
);
</script>

<style lang="scss" module>
@import '@/assets/variables';

$modal-max-width: 396px;
$modal-container-padding: 2rem;

.modalContainer {
  padding: $modal-container-padding;
  overflow: auto;

  @media (min-width: $viewport-medium-min) {
    width: $modal-max-width;
    height: initial;
  }
}

.modalClose {
  top: $space-25;
  padding: $space-75;
}

.form {
  display: flex;
  flex-direction: column;
}

.intro {
  @include body-2;

  margin: 0 0 $space-100 0;
  color: $color-black;
}

.input {
  margin-top: $space-75;
  margin-bottom: $space-75;
}

.legalText {
  @include body-3;

  margin: $space-100 0 0 0;
  color: $color-black;

  a {
    color: inherit;
    text-decoration: underline;
  }
}

.loggedInSection {
  margin-top: $space-75;
  margin-bottom: $space-75;
}

.loggedInText {
  @include body-2;

  margin: 0;
  color: $color-black;
  white-space: pre-line;
}

.logoutLink {
  color: $color-blue-60;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
}

.saveButton {
  width: 100%;
}
</style>
